/**
 * InventoryRequestView (P5a) — the one portal page a CLIENT writes on.
 *
 * ---------------------------------------------------------------------------
 * WHY IT LIVES BEHIND /document/:token LIKE EVERY OTHER PORTAL PAGE
 * ---------------------------------------------------------------------------
 * portalLinks.portalPath() maps every non-estimate target to /document/:token,
 * so every inventory_request link the "Request inventory" button has ever
 * emailed points at that URL. A new top-level route would have dead-linked
 * whatever is already sitting in a client's inbox, to buy nothing: the token
 * resolves the type server-side either way, and this shell already carries the
 * branding, the phone number and the gone-vs-invalid distinction.
 *
 * The WRITE endpoints are a different matter and live under
 * /portal/inventory/:token. Nobody types those; the page calls them.
 *
 * ---------------------------------------------------------------------------
 * NO CUBIC FEET, NO WEIGHT, NO DENSITY — AND NOT BECAUSE THIS FILE HIDES THEM
 * ---------------------------------------------------------------------------
 * There is no code below that drops a measurement, because the payload never
 * carries one: lib/clientInventory.ts does not select those columns. A client
 * reading the network tab sees exactly what they see on the page. Adding a
 * number here would mean changing the shaper first, which is the point.
 *
 * ---------------------------------------------------------------------------
 * EVERY WRITE GOES STRAIGHT TO THE SERVER
 * ---------------------------------------------------------------------------
 * There is no Save button for the list and no draft held in the tab. A client
 * filling this in on a phone gets interrupted — that is the normal case, not
 * the edge one — and coming back to a list that kept everything is the whole
 * reason to build this instead of asking them to phone us. Each change posts
 * and the page refetches, so the list on screen is the list we have.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PortalActions } from './PortalActions';
import { CatalogArt, RoomIcon, Icons } from '../../lib/icons';
import { formatDateTime } from '../../lib/utils';
import { MediaSection, ClientMedia } from './InventoryMedia';
import { useItemSync, SyncStatus } from './useItemSync';
import { prepare, search as searchCatalog, normalize } from '../../lib/inventorySearch';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/**
 * ONE SIZE FOR THE GRID AND ONE FOR THE LISTS, declared here rather than at the
 * five call sites. Artwork and icon render at the same box, so a catalog that is
 * only half illustrated still lays out as a regular grid — and a phone thumb
 * needs the picture large enough to tell a loveseat from a sectional.
 */
const ART = 44;
const ART_LINE = 26;

/**
 * The client's choice of editor. The stored values predate the names on screen:
 * 'cube_sheet' is "Full list", and 'catalog' (migration 044) is "Cube sheet".
 */
export type CapturePath = 'rooms' | 'cube_sheet' | 'catalog';

/** Keys into inventory.notes that are not a room. */
const WRITTEN_LIST = 'written_list';
const GENERAL = 'general';

export interface ClientItem {
  id: string;
  catalogItemId: string | null;
  name: string;
  quantity: number;
  roomKey: string | null;
  notes: string | null;
  icon: string | null;
  /** The illustration, or null to fall back to `icon`. Never required. */
  imageUrl: string | null;
  mine: boolean;
  /** On the editing page only (useItemSync): where this row's save has got to. */
  status?: SyncStatus;
  error?: string | null;
}
export interface ClientRoom {
  key: string; name: string; icon: string | null; items: ClientItem[];
  /**
   * A section that is not a room (045/046): Boxes / Misc. Shown ONCE, before
   * the rooms, never inside them; its items count across every row whatever
   * room they were filed under, and new ones are written with no room.
   */
  wholeMove?: boolean;
}
export interface ClientInventory {
  submittedAt: string | null;
  changeRequestedAt: string | null;
  capturePath: CapturePath | null;
  notes: Record<string, string>;
  rooms: ClientRoom[];
  unroomed: ClientItem[];
  itemCount: number;
  catalog: {
    id: string; name: string; icon: string | null; imageUrl: string | null; rooms: string[];
    /** The one room_key the cube sheet groups this under. Null on an older backend. */
    category?: string | null;
    /** Other names for it (045) — matched by search, never shown. */
    searchTerms?: string[];
  }[];
  media: ClientMedia[];
}

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

/**
 * Saves on BLUR, not on every keystroke. A per-character PATCH on a phone with
 * a flaky signal is a queue of requests that can land out of order and put back
 * a half-typed sentence.
 */
function NotesBox({ label, hint, value, onSave, disabled }: {
  label: string; hint?: string; value: string; disabled?: boolean;
  onSave: (v: string) => void;
}) {
  const [v, setV] = useState(value);
  React.useEffect(() => { setV(value); }, [value]);
  return (
    <label className="ci-note">
      <span>{label}</span>
      <textarea
        value={v}
        disabled={disabled}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { if (v !== value) onSave(v); }}
      />
      {hint && <div className="ci-note-h">{hint}</div>}
    </label>
  );
}

/**
 * A row's save, when it failed: the count stays where the client put it, and
 * this says so, with the way to send it again. Nothing while saving — the bar at
 * the bottom says "Saving…" once for the whole page.
 */
function RowSave({ status, error, onRetry }: { status?: SyncStatus; error?: string | null; onRetry: () => void }) {
  if (status !== 'error') return null;
  return (
    <div className="ci-unsaved" onClick={(e) => e.stopPropagation()}>
      <span>{error || 'Not saved'}</span>
      <button type="button" onClick={onRetry}>Retry</button>
    </div>
  );
}

function AddYourOwn({ onAdd, disabled, placeholder }: {
  onAdd: (name: string) => void; disabled?: boolean; placeholder: string;
}) {
  const [name, setName] = useState('');
  const submit = () => {
    const n = name.trim();
    if (!n) return;
    setName('');
    onAdd(n);
  };
  return (
    <div className="ci-add">
      <input
        value={name} placeholder={placeholder} disabled={disabled}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
      />
      <button type="button" onClick={submit} disabled={disabled || !name.trim()}>Add</button>
    </div>
  );
}

/**
 * ALL THREE CAPTURE PATHS, ALWAYS ON SCREEN, until the list is sent.
 *
 * The first screen asks which way they'd like to work, and that question used to
 * be the only place both options appeared — afterwards a single text link
 * offered the other one, which reads as a detour rather than a choice that is
 * still open. A client who starts room by room, finds it slow on a phone and
 * wants to type the rest should not have to recognize a link as the way out.
 *
 * SWITCHING COSTS THEM NOTHING, which is what makes a toggle honest here: both
 * paths add to the same inventory_item rows, so the tiles tapped in the kitchen
 * are still there in the full list's count, and coming back shows them again.
 * The only thing the path decides is which editor is on screen.
 */
function CapturePathToggle({ path, busy, onPick }: {
  path: CapturePath; busy: boolean;
  onPick: (p: CapturePath) => void;
}) {
  return (
    <>
      <div className="ci-toggle" role="group" aria-label="How you would like to fill this in">
        <button
          type="button" className={`ci-tog${path === 'rooms' ? ' on' : ''}`}
          aria-pressed={path === 'rooms'} disabled={busy} onClick={() => onPick('rooms')}
        >
          <RoomIcon name="layout-grid" size={18} stroke={1.7} />
          <span>Room by room</span>
        </button>
        <button
          type="button" className={`ci-tog${path === 'cube_sheet' ? ' on' : ''}`}
          aria-pressed={path === 'cube_sheet'} disabled={busy} onClick={() => onPick('cube_sheet')}
        >
          <Icons.document size={18} stroke={1.7} />
          <span>Full list</span>
        </button>
        <button
          type="button" className={`ci-tog${path === 'catalog' ? ' on' : ''}`}
          aria-pressed={path === 'catalog'} disabled={busy} onClick={() => onPick('catalog')}
        >
          <Icons.box size={18} stroke={1.7} />
          <span>Cube sheet</span>
        </button>
      </div>
      <p className="ci-toggle-h">
        Switch whenever you like — everything you've added stays on your list either way.
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function InventoryRequestView({ inventory, token }: { inventory: ClientInventory; token: string }) {
  /**
   * `base` is the server's payload: rooms, catalogue, notes, media, the path and
   * the lock. The ITEMS are not read from it after load — they live in
   * useItemSync, which edits them on screen at once and saves behind the thumb.
   * `inv` below is the two put back together, which is what every path draws.
   */
  const [base, setBase] = useState<ClientInventory>(inventory);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);          // header writes only: path, notes, submit
  const [noteState, setNoteState] = useState('');
  const [openRoom, setOpenRoom] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [confirming, setConfirming] = useState(false);

  const call = useCallback(async (path: string, init?: RequestInit) => {
    const res = await fetch(`${API_BASE_URL}/portal/inventory/${encodeURIComponent(token)}${path}`, {
      ...init,
      headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.success) {
      const e: any = new Error(json?.error?.message || 'We could not save that. Please try again.');
      e.status = res.status;
      throw e;
    }
    return json.data;
  }, [token]);

  const sync = useItemSync(call, [...inventory.rooms.flatMap((r) => r.items), ...inventory.unroomed]);

  const inv = useMemo((): ClientInventory => {
    const byRoom = new Map<string, ClientItem[]>();
    const unroomed: ClientItem[] = [];
    const known = new Set(base.rooms.map((r) => r.key));
    for (const it of sync.items) {
      if (it.roomKey && known.has(it.roomKey)) {
        if (!byRoom.has(it.roomKey)) byRoom.set(it.roomKey, []);
        byRoom.get(it.roomKey)!.push(it);
      } else unroomed.push(it);
    }
    return {
      ...base,
      rooms: base.rooms.map((r) => ({ ...r, items: byRoom.get(r.key) ?? [] })),
      unroomed,
      itemCount: sync.items.reduce((s, i) => s + i.quantity, 0),
    };
  }, [base, sync.items]);

  /** The whole payload, items included. Load, path switch and submit only. */
  const { replaceFromServer } = sync;
  const takePayload = useCallback((d: ClientInventory) => {
    setBase(d);
    replaceFromServer([...d.rooms.flatMap((r) => r.items), ...d.unroomed]);
  }, [replaceFromServer]);

  /**
   * Header writes (path, notes) still wait for their answer — they are one tap
   * each, not fifty — but the answer IS the payload, so there is no second
   * request. A notes save takes only the notes from it: the items in that
   * answer may be older than what is on screen.
   */
  const patchHeader = async (body: Record<string, unknown>, take: 'notes' | 'all') => {
    setBusy(true); setErr(''); setNoteState('Saving…');
    try {
      const d: ClientInventory = await call('', { method: 'PATCH', body: JSON.stringify(body) });
      if (take === 'all') takePayload(d);
      else setBase((b) => ({ ...b, notes: d.notes }));
      setNoteState('Saved');
      window.setTimeout(() => setNoteState((s) => (s === 'Saved' ? '' : s)), 1600);
    } catch (e: any) {
      setErr(e?.message || 'We could not save that. Please try again.');
      setNoteState('');
    } finally {
      setBusy(false);
    }
  };

  const saveNote = (key: string, text: string) => patchHeader({ notes: { [key]: text } }, 'notes');

  /**
   * Photos and video upload themselves and then need the server's own entries
   * (ids, signed-URL routes), so this one does fetch — but takes only `media`,
   * and leaves the items alone.
   */
  const refreshMedia = async () => {
    try {
      const d = await call('');
      setBase((b) => ({ ...b, media: d.inventory.media }));
    } catch (e: any) {
      setErr(e?.message || 'We could not refresh your photos. Please reload the page.');
    }
  };

  const catById = useMemo(() => new Map(base.catalog.map((c) => [c.id, c])), [base.catalog]);

  const addCatalog = (catalogItemId: string, roomKey: string | null, qty = 1) => {
    const c = catById.get(catalogItemId);
    if (!c) return;
    sync.add({ catalogItemId, customName: null, name: c.name, roomKey, icon: c.icon, imageUrl: c.imageUrl, qty });
  };

  const addCustom = (name: string, roomKey: string | null) =>
    sync.add({ catalogItemId: null, customName: name, name, roomKey, icon: null, imageUrl: null });

  const setQty = (item: ClientItem, n: number) => sync.setQty(item.id, n);

  // ---- Cube sheet: one count per catalogue item, spread over its rows -------
  //
  // THE STEPPER SHOWS THE TOTAL ACROSS ROWS — the room path can file a fridge
  // in the kitchen and the garage. Up goes onto the client's unroomed row (made
  // if need be); down comes off that first, then their room rows; never below
  // what our team entered. Each row then saves through useItemSync like any
  // other, so the batching and the retry are the same on every path.
  const setCatalogTotal = (catId: string, n: number) => {
    const rows = catalogRows(inv, catId);
    const current = rows.reduce((s, r) => s + r.quantity, 0);
    const target = Math.max(repFloor(rows), Math.min(999, Math.trunc(n) || 0));
    let delta = target - current;
    if (delta === 0) return;

    const mine = rows.filter((r) => r.mine);
    const loose = mine.find((r) => !r.roomKey) ?? null;
    if (delta > 0) {
      if (loose) sync.setQty(loose.id, loose.quantity + delta);
      else addCatalog(catId, null, delta);
      return;
    }
    for (const r of [...(loose ? [loose] : []), ...mine.filter((x) => x !== loose).reverse()]) {
      if (delta >= 0) break;
      const take = Math.min(r.quantity, -delta);
      sync.setQty(r.id, r.quantity - take);
      delta += take;
    }
  };

  const displayTotal = (catId: string) => catalogRows(inv, catId).reduce((s, r) => s + r.quantity, 0);

  // ---- Search (045): one matcher, every path ---------------------------------
  const prepared = useMemo(() => prepare(base.catalog), [base.catalog]);

  /**
   * A search that found nothing, or an item the client then added as their own:
   * told to the server so an admin can see what people look for (Settings →
   * Leads). Fire-and-forget — 204 whatever happens, and a failure is silent,
   * because it is our bookkeeping and not the client's problem. Each term once
   * per visit.
   */
  const reported = useRef<Set<string>>(new Set());
  const reportMiss = useCallback((term: string, kind: 'search' | 'add_own' = 'search') => {
    const key = `${kind}:${normalize(term)}`;
    if (normalize(term).length < 2 || reported.current.has(key)) return;
    reported.current.add(key);
    void fetch(`${API_BASE_URL}/portal/inventory/${encodeURIComponent(token)}/search-miss`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ term, path: base.capturePath ?? 'rooms', kind }),
    }).catch(() => {});
  }, [token, base.capturePath]);

  const searchKit: SearchKit = { prepared, reportMiss };

  // A phone locking its screen or switching app is the last chance to send.
  const flushAllRef = useRef(sync.flushAll);
  flushAllRef.current = sync.flushAll;
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') void flushAllRef.current(); };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, []);

  /** Everything saved, or an error that says what is not. */
  const settle = async () => {
    const ok = await sync.flushAll();
    if (!ok) setErr('Some items did not save — tap Retry on them first.');
    return ok;
  };

  const submit = async () => {
    if (!(await settle())) { setConfirming(false); return; }
    setBusy(true); setErr('');
    try {
      await call('/submit', { method: 'POST' });
      const d = await call('');
      takePayload(d.inventory);
      setConfirming(false);
    } catch (e: any) {
      setErr(e?.message || 'We could not send that. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // ---- Submitted: read-only, and the only way back in is to ask ------------
  if (base.submittedAt) {
    return <SubmittedSummary inv={inv} token={token} call={call} onChanged={takePayload} />;
  }

  const path = base.capturePath;

  // ---- The chooser ---------------------------------------------------------
  if (!path) {
    return (
      <>
        <div className="pp-card">
          <div className="pp-sec">How would you like to do this?</div>
          <p className="pp-sub" style={{ marginBottom: 14 }}>
            Any of these works, and you can switch at any time before you send it to us.
          </p>
          <div className="ci-choose">
            <button type="button" className="ci-choice" disabled={busy} onClick={() => patchHeader({ capture_path: 'rooms' }, 'all')}>
              <RoomIcon name="layout-grid" size={34} stroke={1.6} />
              <span className="ci-choice-t">Room by room</span>
              <span className="ci-choice-b">Walk through your home one room at a time and tap the things you're taking.</span>
            </button>
            <button type="button" className="ci-choice" disabled={busy} onClick={() => patchHeader({ capture_path: 'cube_sheet' }, 'all')}>
              <Icons.document size={34} stroke={1.6} />
              <span className="ci-choice-t">Full list</span>
              <span className="ci-choice-b">Type it all out in one box, or search for items and add them as you go.</span>
            </button>
            <button type="button" className="ci-choice" disabled={busy} onClick={() => patchHeader({ capture_path: 'catalog' }, 'all')}>
              <Icons.box size={34} stroke={1.6} />
              <span className="ci-choice-t">Cube sheet</span>
              <span className="ci-choice-b">Our whole list of household items in one place — set a number next to each thing you have.</span>
            </button>
          </div>
          <div className="ci-state err" style={{ marginTop: 10 }}>{err}</div>
        </div>
        <MediaSection media={base.media} token={token} onChanged={refreshMedia} />
      </>
    );
  }

  // Counts still waiting go first, so the next editor — and the payload the
  // switch brings back — already has them. Tapping the current path is not a
  // switch and does not post.
  const pickPath = async (p: CapturePath) => {
    if (p === path) return;
    await sync.flushAll();
    await patchHeader({ capture_path: p }, 'all');
  };

  const status = sync.failed
    ? `${sync.failed} not saved — tap Retry`
    : sync.saving ? 'Saving…' : noteState;

  return (
    <>
      <CapturePathToggle path={path} busy={busy} onPick={pickPath} />

      {path === 'rooms' ? (
        <RoomsPath
          inv={inv} busy={busy} openRoom={openRoom} setOpenRoom={setOpenRoom}
          onAddCatalog={addCatalog} onAddCustom={addCustom} onQty={setQty} onRetry={sync.retry} onNote={saveNote}
          total={displayTotal} onSetTotal={setCatalogTotal} kit={searchKit}
        />
      ) : path === 'catalog' ? (
        <CatalogPath
          inv={inv} busy={busy} search={search} setSearch={setSearch}
          total={displayTotal} onSetTotal={setCatalogTotal}
          onAddCustom={addCustom} onQty={setQty} onRetry={sync.retry} onNote={saveNote} kit={searchKit}
        />
      ) : (
        <CubeSheetPath
          inv={inv} busy={busy} search={search} setSearch={setSearch}
          onAddCatalog={addCatalog} onAddCustom={addCustom} onQty={setQty} onRetry={sync.retry} onNote={saveNote}
          total={displayTotal} onSetTotal={setCatalogTotal} kit={searchKit}
        />
      )}

      <div className="pp-card">
        <div className="pp-sec">Anything else we should know?</div>
        <NotesBox
          label="Notes for our team"
          hint="Stairs, a narrow driveway, something fragile, a date that might move — anything that helps us plan."
          value={base.notes[GENERAL] ?? ''}
          disabled={busy}
          onSave={(v) => saveNote(GENERAL, v)}
        />
      </div>

      <MediaSection media={base.media} token={token} onChanged={refreshMedia} />

      <div className="ci-bar">
        <div className="ci-state err">{err}</div>
        {confirming ? (
          <>
            <div className="ci-bar-c">
              Once you send this, the list locks so we can price it. If you need to change something
              afterwards, just ask us to reopen it — it takes a moment.
            </div>
            <PortalActions style={{ marginTop: 0 }}>
              <button type="button" className="pp-btn primary" disabled={busy} onClick={submit}>
                Yes, send it
              </button>
              <button type="button" className="pp-btn pp-btn-quiet" disabled={busy} onClick={() => setConfirming(false)}>
                Keep editing
              </button>
            </PortalActions>
          </>
        ) : (
          <>
            <div className="ci-bar-c">
              <span className="ci-count">{inv.itemCount} item{inv.itemCount === 1 ? '' : 's'} so far.</span>
              {' '}Everything saves as you go — you can close this and come back.
              {status && <> · <span className={`ci-state${sync.failed ? ' err' : ''}`} style={{ display: 'inline' }}>{status}</span></>}
            </div>
            <PortalActions style={{ marginTop: 0 }}>
              <button
                type="button" className="pp-btn primary" disabled={busy}
                onClick={() => { void sync.flushAll(); setConfirming(true); }}
              >
                <Icons.check size={18} /> Send my inventory
              </button>
            </PortalActions>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Room by room
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Shared by the paths: search, and the whole-move section (Boxes / Misc)
// ---------------------------------------------------------------------------

interface SearchKit {
  prepared: ReturnType<typeof prepare<ClientInventory['catalog'][number]>>;
  reportMiss: (term: string, kind?: 'search' | 'add_own') => void;
}

/** The whole-move section, if this company has one (migration 046 adds it). */
function wholeMoveSection(inv: ClientInventory): ClientRoom | null {
  return inv.rooms.find((r) => r.wholeMove) ?? null;
}

/** Catalogue ids that belong to the whole-move section — never shown inside a room. */
function sectionIds(inv: ClientInventory): Set<string> {
  const sec = wholeMoveSection(inv);
  return new Set(sec ? inv.catalog.filter((c) => c.category === sec.key).map((c) => c.id) : []);
}

/** What our team entered per catalogue item — the client's floor for that item. */
function repFloors(inv: ClientInventory): Record<string, number> {
  const m: Record<string, number> = {};
  for (const it of [...inv.rooms.flatMap((r) => r.items), ...inv.unroomed]) {
    if (it.catalogItemId && !it.mine) m[it.catalogItemId] = (m[it.catalogItemId] ?? 0) + it.quantity;
  }
  return m;
}

/**
 * Tell the server about a search that found nothing — once it has sat unchanged
 * for 1.5s, so a word being typed is not logged letter by letter.
 */
function useZeroResultReport(query: string, found: number, report: SearchKit['reportMiss']) {
  useEffect(() => {
    const q = query.trim();
    if (!q || found > 0 || normalize(q).length < 2) return;
    const t = window.setTimeout(() => report(q), 1500);
    return () => window.clearTimeout(t);
  }, [query, found, report]);
}

/** "Can't find it?" — one tap adds exactly what they typed as their own item. */
function NoMatch({ query, onAdd }: { query: string; onAdd: () => void }) {
  return (
    <button type="button" className="ci-nomatch" onClick={onAdd}>
      <span className="ci-nomatch-t">Can't find it?</span>
      <span>Add “{query.trim()}” as your own item</span>
    </button>
  );
}

/** One catalogue line with its count — the cube sheet's row, used by the section too. */
function CatalogLine({ c, inv, qty, floor, onSet, onRetry }: {
  c: ClientInventory['catalog'][number]; inv: ClientInventory; qty: number; floor: number;
  onSet: (n: number) => void; onRetry: (key: string) => void;
}) {
  // One line can be several rows; any of them failing is this line failing,
  // and Retry sends them all.
  const failed = catalogRows(inv, c.id).filter((r) => r.status === 'error');
  return (
    <div className={`cs-row${qty > 0 ? ' on' : ''}${failed.length ? ' bad' : ''}`}>
      <CatalogArt src={c.imageUrl} icon={c.icon} size={ART} />
      <div className="cs-row-n">
        {c.name}
        {/* Why − stops above zero: those are ours. */}
        {floor > 0 && <span className="ci-ours">Added by our team</span>}
        {failed.length > 0 && (
          <RowSave status="error" error={failed[0].error} onRetry={() => failed.forEach((r) => onRetry(r.id))} />
        )}
      </div>
      <CountStepper qty={qty} floor={floor} label={c.name} onSet={onSet} />
    </div>
  );
}

/**
 * BOXES / MISC — ONE SECTION FOR THE WHOLE MOVE, first on every path.
 *
 * Boxes are counted for the move, not per room: "how many boxes in the
 * kitchen" is a question nobody can answer before packing, and asking it in
 * eleven rooms produced eleven guesses. Each line is the TOTAL across every
 * row for that item, so counts a client entered per room before this section
 * existed still add up here; a change goes onto the client's own unroomed row
 * (see setCatalogTotal), the same rule the cube sheet uses.
 *
 * Batched, optimistic and typeable like every other count on this page.
 */
function WholeMoveSection({ inv, section, busy, total, onSetTotal, onRetry, onNote }: {
  inv: ClientInventory; section: ClientRoom; busy: boolean;
  total: (catId: string) => number;
  onSetTotal: (catId: string, n: number) => void;
  onRetry: (key: string) => void;
  onNote: (key: string, text: string) => void;
}) {
  const items = inv.catalog.filter((c) => c.category === section.key);
  if (!items.length) return null;
  const floors = repFloors(inv);
  const count = items.reduce((s, c) => s + total(c.id), 0);
  return (
    <section className="pp-card cs-sec ci-whole" data-key={section.key}>
      <div className="cs-sec-h">
        <RoomIcon name={section.icon || 'box'} size={20} stroke={1.8} />
        <div className="pp-sec">{section.name}</div>
        {count > 0 && <span className="ci-room-c">{count}</span>}
      </div>
      <p className="pp-sub" style={{ marginBottom: 10 }}>
        For the whole move — count them once here, not room by room. A rough number is fine.
      </p>
      <div className="cs-rows">
        {items.map((c) => (
          <CatalogLine
            key={c.id} c={c} inv={inv} qty={total(c.id)} floor={floors[c.id] ?? 0}
            onSet={(n) => onSetTotal(c.id, n)} onRetry={onRetry}
          />
        ))}
      </div>
      <SectionNote roomKey={section.key} name={section.name} value={inv.notes[section.key] ?? ''} busy={busy} onNote={onNote} />
    </section>
  );
}

interface Tile { key: string; name: string; icon: string | null; imageUrl: string | null; catId: string | null; item: ClientItem | null }

/**
 * A room's grid is its catalogue subset PLUS anything already in the room that
 * the subset does not list — an item the client filed here from a search, or a
 * custom one they named. A row that exists and does not render is a row the
 * client cannot correct.
 */
function tilesFor(roomKey: string, items: ClientItem[], catalog: ClientInventory['catalog'], exclude: Set<string>): Tile[] {
  const byCat = new Map<string, ClientItem>();
  const customs: ClientItem[] = [];
  for (const it of items) {
    // A whole-move item filed in this room before the section existed is
    // counted in the section, so it is not drawn here a second time.
    if (it.catalogItemId && exclude.has(it.catalogItemId)) continue;
    if (it.catalogItemId) byCat.set(it.catalogItemId, it); else customs.push(it);
  }
  const subset = catalog.filter((c) => c.rooms.includes(roomKey) && !exclude.has(c.id));
  const listed = new Set(subset.map((c) => c.id));
  const tiles: Tile[] = subset.map((c) => ({
    key: `c${c.id}`, name: c.name, icon: c.icon, imageUrl: c.imageUrl, catId: c.id, item: byCat.get(c.id) ?? null,
  }));
  byCat.forEach((it, catId) => {
    if (!listed.has(catId)) tiles.push({ key: `x${it.id}`, name: it.name, icon: it.icon, imageUrl: it.imageUrl, catId, item: it });
  });
  for (const it of customs) tiles.push({ key: `u${it.id}`, name: it.name, icon: it.icon, imageUrl: it.imageUrl, catId: null, item: it });
  return tiles;
}

function RoomsPath({ inv, busy, openRoom, setOpenRoom, onAddCatalog, onAddCustom, onQty, onRetry, onNote, total, onSetTotal, kit }: {
  inv: ClientInventory; busy: boolean;
  openRoom: string | null; setOpenRoom: (k: string | null) => void;
  onAddCatalog: (catId: string, roomKey: string | null) => void;
  onAddCustom: (name: string, roomKey: string | null) => void;
  onQty: (item: ClientItem, n: number) => void;
  onRetry: (key: string) => void;
  onNote: (key: string, text: string) => void;
  total: (catId: string) => number;
  onSetTotal: (catId: string, n: number) => void;
  kit: SearchKit;
}) {
  const section = wholeMoveSection(inv);
  const inSection = sectionIds(inv);
  const unroomed = inv.unroomed.filter((it) => !(it.catalogItemId && inSection.has(it.catalogItemId)));
  return (
    <>
      {section && (
        <WholeMoveSection
          inv={inv} section={section} busy={busy} total={total}
          onSetTotal={onSetTotal} onRetry={onRetry} onNote={onNote}
        />
      )}

      {inv.rooms.filter((r) => !r.wholeMove).map((room) => {
        const open = openRoom === room.key;
        const count = room.items
          .filter((i) => !(i.catalogItemId && inSection.has(i.catalogItemId)))
          .reduce((s, i) => s + i.quantity, 0);
        return (
          <div key={room.key} className={`ci-room${open ? ' open' : ''}`}>
            <button
              type="button" className="ci-room-h" aria-expanded={open}
              onClick={() => setOpenRoom(open ? null : room.key)}
            >
              <RoomIcon name={room.icon} size={22} stroke={1.8} />
              <span className="ci-room-n">{room.name}</span>
              {count > 0 && <span className="ci-room-c">{count}</span>}
              {open ? <Icons.chevronUp size={18} /> : <Icons.chevronDown size={18} />}
            </button>

            {open && (
              <div className="ci-room-b">
                <RoomSearch
                  room={room} inv={inv} kit={kit} inSection={inSection} total={total}
                  onAddCatalog={onAddCatalog} onAddCustom={onAddCustom} onQty={onQty} onSetTotal={onSetTotal}
                />

                <div className="ci-grid">
                  {tilesFor(room.key, room.items, inv.catalog, inSection).map((t) => (
                    <TileButton
                      key={t.key} tile={t}
                      onAdd={() => t.catId && onAddCatalog(t.catId, room.key)}
                      onQty={onQty} onRetry={onRetry}
                    />
                  ))}
                </div>

                <AddYourOwn
                  placeholder={`Something else in the ${room.name.toLowerCase()}…`}
                  onAdd={(n) => onAddCustom(n, room.key)}
                />

                <NotesBox
                  label={`Notes about the ${room.name.toLowerCase()}`}
                  value={inv.notes[room.key] ?? ''}
                  disabled={busy}
                  onSave={(v) => onNote(room.key, v)}
                />
              </div>
            )}
          </div>
        );
      })}

      {unroomed.length > 0 && (
        <div className="pp-card">
          <div className="pp-sec">Not in a room</div>
          <div className="ci-list">
            {unroomed.map((it) => (
              <LineRow key={it.id} item={it} onQty={onQty} onRetry={onRetry} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Search the WHOLE catalogue from inside a room — the room's grid is only what
 * is usual there, and the thing a client is looking at may not be. A pick goes
 * into this room, or into Boxes / Misc if that is where the item lives; one
 * already in the room is counted up rather than added twice.
 */
function RoomSearch({ room, inv, kit, inSection, total, onAddCatalog, onAddCustom, onQty, onSetTotal }: {
  room: ClientRoom; inv: ClientInventory; kit: SearchKit; inSection: Set<string>;
  total: (catId: string) => number;
  onAddCatalog: (catId: string, roomKey: string | null) => void;
  onAddCustom: (name: string, roomKey: string | null) => void;
  onQty: (item: ClientItem, n: number) => void;
  onSetTotal: (catId: string, n: number) => void;
}) {
  const [q, setQ] = useState('');
  const hits = useMemo(() => (q.trim() ? searchCatalog(kit.prepared, q).slice(0, 12) : []), [q, kit.prepared]);
  useZeroResultReport(q, hits.length, kit.reportMiss);

  const pick = (catId: string) => {
    if (inSection.has(catId)) onSetTotal(catId, total(catId) + 1);
    else {
      const here = room.items.find((i) => i.catalogItemId === catId);
      if (here) onQty(here, here.quantity + 1); else onAddCatalog(catId, room.key);
    }
    setQ('');
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <input
        className="ci-search" type="search" value={q} style={{ marginBottom: q.trim() ? 10 : 0 }}
        placeholder={`Search everything — couch, fridge, boxes…`} aria-label={`Search for something in the ${room.name.toLowerCase()}`}
        onChange={(e) => setQ(e.target.value)}
      />
      {q.trim() && (hits.length > 0 ? (
        <div className="ci-grid">
          {hits.map(({ item: c }) => (
            <button key={c.id} type="button" className="ci-tile" onClick={() => pick(c.id)}>
              <CatalogArt src={c.imageUrl} icon={c.icon} size={ART} />
              <span className="ci-tile-n">{c.name}</span>
              {inSection.has(c.id) && <span className="ci-ours">{wholeMoveSection(inv)?.name}</span>}
            </button>
          ))}
        </div>
      ) : (
        <NoMatch query={q} onAdd={() => { kit.reportMiss(q, 'add_own'); onAddCustom(q.trim(), room.key); setQ(''); }} />
      ))}
    </div>
  );
}

/**
 * A tile is never disabled while something saves: the tap is counted on screen
 * at once and sent behind it (useItemSync). Locking the grid per round trip is
 * what used to drop taps.
 */
function TileButton({ tile, onAdd, onQty, onRetry }: {
  tile: Tile; onAdd: () => void;
  onQty: (item: ClientItem, n: number) => void;
  onRetry: (key: string) => void;
}) {
  const item = tile.item;
  if (!item) {
    return (
      <button type="button" className="ci-tile" onClick={onAdd}>
        <CatalogArt src={tile.imageUrl} icon={tile.icon} size={ART} />
        <span className="ci-tile-n">{tile.name}</span>
      </button>
    );
  }
  return (
    <div className={`ci-tile on${item.status === 'error' ? ' bad' : ''}`}>
      <CatalogArt src={tile.imageUrl} icon={tile.icon} size={ART} />
      <span className="ci-tile-n">{tile.name}</span>
      <CountStepper
        compact qty={item.quantity} floor={item.mine ? 0 : 1} label={tile.name}
        onSet={(n) => onQty(item, n)}
      />
      <RowSave status={item.status} error={item.error} onRetry={() => onRetry(item.id)} />
    </div>
  );
}

function LineRow({ item, onQty, onRetry }: {
  item: ClientItem; onQty: (item: ClientItem, n: number) => void; onRetry: (key: string) => void;
}) {
  return (
    <div className={`ci-line${item.status === 'error' ? ' bad' : ''}`}>
      <CatalogArt src={item.imageUrl} icon={item.icon} size={ART_LINE} stroke={1.8} />
      <div className="ci-line-m">
        <div className="ci-line-n">
          {item.name}
          {/* Ours, so they know why it is there and why it will not delete. */}
          {!item.mine && <span className="ci-ours">Added by our team</span>}
        </div>
        {item.notes && <div className="ci-line-s">{item.notes}</div>}
        <RowSave status={item.status} error={item.error} onRetry={() => onRetry(item.id)} />
      </div>
      <CountStepper qty={item.quantity} floor={item.mine ? 0 : 1} label={item.name} onSet={(n) => onQty(item, n)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cube sheet  (capture_path 'catalog', migration 044)
// ---------------------------------------------------------------------------

/** Every row for one catalogue item, wherever it was filed. */
function catalogRows(inv: ClientInventory, catId: string): ClientItem[] {
  const out: ClientItem[] = [];
  for (const r of inv.rooms) for (const it of r.items) if (it.catalogItemId === catId) out.push(it);
  for (const it of inv.unroomed) if (it.catalogItemId === catId) out.push(it);
  return out;
}

/** What our team put down for an item. The client's stepper stops there. */
function repFloor(rows: ClientItem[]): number {
  return rows.filter((r) => !r.mine).reduce((s, r) => s + r.quantity, 0);
}

/** The group for a catalogue row whose category is not one of the rooms. */
const OTHER_GROUP = '_other';

/**
 * − / number / +, on every path, with the number typeable — "40 boxes" is not
 * forty taps. The floor is 0 on the client's own row and 1 on ours: stepping to
 * zero deletes, and the server refuses a client deleting a row our team entered.
 * Commits on blur or Enter, like the notes, so a half-typed "4" on the way to
 * "40" is never sent.
 */
function CountStepper({ qty, floor, label, onSet, compact }: {
  qty: number; floor: number; label: string; onSet: (n: number) => void;
  /** The tile size: 30px buttons, to fit three across a 96px tile. */
  compact?: boolean;
}) {
  const [text, setText] = useState(String(qty));
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setText(String(qty)); }, [qty, editing]);

  const commit = () => {
    setEditing(false);
    const n = Math.max(floor, Math.min(999, Math.trunc(Number(text)) || 0));
    setText(String(n));
    if (n !== qty) onSet(n);
  };

  return (
    <div className={compact ? 'ci-step' : 'cs-step'} onClick={(e) => e.stopPropagation()}>
      <button type="button" aria-label={`One fewer ${label}`} disabled={qty <= floor} onClick={() => onSet(qty - 1)}>−</button>
      <input
        type="text" inputMode="numeric" pattern="[0-9]*" aria-label={`How many: ${label}`}
        value={text}
        onFocus={(e) => { setEditing(true); e.target.select(); }}
        onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      />
      <button type="button" aria-label={`One more ${label}`} disabled={qty >= 999} onClick={() => onSet(qty + 1)}>+</button>
    </div>
  );
}

/** A category's notes: one tap away rather than 11 empty boxes down the page. */
function SectionNote({ roomKey, name, value, busy, onNote }: {
  roomKey: string; name: string; value: string; busy: boolean;
  onNote: (key: string, text: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!open && !value.trim()) {
    return (
      <button type="button" className="cs-note-btn" onClick={() => setOpen(true)}>
        + Add a note about the {name.toLowerCase()}
      </button>
    );
  }
  return (
    <NotesBox
      label={`Notes about the ${name.toLowerCase()}`}
      value={value} disabled={busy}
      onSave={(v) => onNote(roomKey, v)}
    />
  );
}

/**
 * The whole catalogue, grouped by inventory_catalog.category — which is a room
 * key, so every item appears exactly once and the groups are the rooms the
 * client already knows from the other path. Category notes share the room's
 * notes key for the same reason: "notes about the kitchen" is one note.
 *
 * NO MEASUREMENTS, as everywhere on this page: the payload has none to show.
 *
 * Steppers here are NOT disabled while another write is saving. The counts are
 * batched by the page (see setCatalogTotal), and disabling 170 buttons for each
 * round trip is what made rapid tapping drop taps in the first place.
 */
function CatalogPath({ inv, busy, search, setSearch, total, onSetTotal, onAddCustom, onQty, onRetry, onNote, kit }: {
  inv: ClientInventory; busy: boolean; search: string; setSearch: (s: string) => void;
  total: (catId: string) => number;
  onSetTotal: (catId: string, n: number) => void;
  onAddCustom: (name: string, roomKey: string | null) => void;
  onQty: (item: ClientItem, n: number) => void;
  onRetry: (key: string) => void;
  onNote: (key: string, text: string) => void;
  kit: SearchKit;
}) {
  const q = search.trim();

  // Floors are per item and change only when the server's rows do.
  const floors = useMemo(() => repFloors(inv), [inv]);

  const groups = useMemo(() => {
    const known = new Set(inv.rooms.map((r) => r.key));
    // The whole-move section (Boxes / Misc) first, whatever its sort_order.
    const ordered = [...inv.rooms.filter((r) => r.wholeMove), ...inv.rooms.filter((r) => !r.wholeMove)];
    const out = ordered.map((r) => ({
      key: r.key, name: r.name, icon: r.icon as string | null, room: true, whole: !!r.wholeMove,
      items: inv.catalog.filter((c) => c.category === r.key),
    }));
    // An item whose category is not a room (or an older backend that does not
    // send one) still gets a line: an item the page cannot show is one the
    // client cannot count.
    const rest = inv.catalog.filter((c) => !c.category || !known.has(c.category));
    if (rest.length) out.push({ key: OTHER_GROUP, name: 'Other', icon: null, room: false, whole: false, items: rest });
    return out;
  }, [inv.rooms, inv.catalog]);

  // Searching keeps the groups but filters and RANKS within them: exact name
  // matches before synonyms before typo matches (lib/inventorySearch).
  const shown = useMemo(() => {
    if (!q) return groups;
    const rank = new Map(searchCatalog(kit.prepared, q).map((h, i) => [h.item.id, i]));
    return groups
      .map((g) => ({ ...g, items: g.items.filter((c) => rank.has(c.id)).sort((a, b) => rank.get(a.id)! - rank.get(b.id)!) }))
      .filter((g) => g.items.length > 0);
  }, [groups, q, kit.prepared]);

  // Rows the catalogue lines do not cover: custom items, ours or theirs, and a
  // catalogue row whose item has since left the catalogue.
  const others = useMemo(() => {
    const ids = new Set(inv.catalog.map((c) => c.id));
    return [...inv.rooms.flatMap((r) => r.items), ...inv.unroomed]
      .filter((it) => !it.catalogItemId || !ids.has(it.catalogItemId))
      .filter((it) => !q || normalize(it.name).includes(normalize(q)));
  }, [inv.rooms, inv.unroomed, inv.catalog, q]);

  useZeroResultReport(search, shown.reduce((n, g) => n + g.items.length, 0) + others.length, kit.reportMiss);

  // ---- the jump bar: follow the scroll, and jump on tap --------------------
  const [active, setActive] = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const secRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      const top = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (top) setActive((top.target as HTMLElement).dataset.key ?? null);
    }, { rootMargin: '-130px 0px -55% 0px' });
    for (const key of [...shown.map((g) => g.key), '_yours']) {
      const el = secRefs.current[key];
      if (el) io.observe(el);
    }
    return () => io.disconnect();
  }, [shown]);

  // Keep the active chip in view sideways, without moving the page vertically
  // (scrollIntoView would).
  useEffect(() => {
    const bar = barRef.current;
    const chip = active ? bar?.querySelector<HTMLElement>(`[data-chip="${active}"]`) : null;
    if (bar && chip) bar.scrollTo({ left: Math.max(0, chip.offsetLeft - 16), behavior: 'smooth' });
  }, [active]);

  const jump = (key: string) => {
    setActive(key);
    secRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const groupCount = (items: { id: string }[]) => items.reduce((s, c) => s + total(c.id), 0);

  return (
    <>
      <div className="cs-top">
        <input
          className="ci-search" type="search" value={search}
          placeholder="Search — sofa, fridge, boxes…" aria-label="Search the list"
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="cs-jump" ref={barRef} role="navigation" aria-label="Jump to a category">
          {shown.map((g) => {
            const n = groupCount(g.items);
            return (
              <button
                key={g.key} type="button" data-chip={g.key}
                className={`cs-chip${active === g.key ? ' on' : ''}`}
                onClick={() => jump(g.key)}
              >
                {g.name}
                {n > 0 && <span className="cs-chip-c">{n}</span>}
              </button>
            );
          })}
          {others.length > 0 && (
            <button
              type="button" data-chip="_yours"
              className={`cs-chip${active === '_yours' ? ' on' : ''}`}
              onClick={() => jump('_yours')}
            >
              Your own items
            </button>
          )}
        </div>
      </div>

      {q && shown.length === 0 && others.length === 0 && (
        <div style={{ marginBottom: 12 }}>
          <NoMatch query={q} onAdd={() => { kit.reportMiss(q, 'add_own'); onAddCustom(q, null); setSearch(''); }} />
        </div>
      )}

      {shown.map((g) => {
        const n = groupCount(g.items);
        return (
          <section
            key={g.key} data-key={g.key} className="pp-card cs-sec"
            ref={(el) => { secRefs.current[g.key] = el; }}
          >
            <div className="cs-sec-h">
              {g.icon && <RoomIcon name={g.icon} size={20} stroke={1.8} />}
              <div className="pp-sec">{g.name}</div>
              {n > 0 && <span className="ci-room-c">{n}</span>}
            </div>
            {g.whole && !q && (
              <p className="pp-sub" style={{ marginBottom: 10 }}>For the whole move — count them once here, not room by room.</p>
            )}
            <div className="cs-rows">
              {g.items.map((c) => (
                <CatalogLine
                  key={c.id} c={c} inv={inv} qty={total(c.id)} floor={floors[c.id] ?? 0}
                  onSet={(v) => onSetTotal(c.id, v)} onRetry={onRetry}
                />
              ))}
            </div>
            {g.room && !q && (
              <SectionNote roomKey={g.key} name={g.name} value={inv.notes[g.key] ?? ''} busy={busy} onNote={onNote} />
            )}
          </section>
        );
      })}

      <section
        data-key="_yours" className="pp-card cs-sec"
        ref={(el) => { secRefs.current._yours = el; }}
      >
        <div className="pp-sec">Your own items</div>
        <p className="pp-sub" style={{ marginBottom: 10 }}>Something not on our list? Add it by name.</p>
        {others.length > 0 && (
          <div className="ci-list" style={{ marginBottom: 4 }}>
            {others.map((it) => <LineRow key={it.id} item={it} onQty={onQty} onRetry={onRetry} />)}
          </div>
        )}
        <AddYourOwn placeholder="Add something by name…" onAdd={(nm) => onAddCustom(nm, null)} />
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Full list  (capture_path 'cube_sheet' — the stored value predates the name)
// ---------------------------------------------------------------------------

function CubeSheetPath({ inv, busy, search, setSearch, onAddCatalog, onAddCustom, onQty, onRetry, onNote, total, onSetTotal, kit }: {
  inv: ClientInventory; busy: boolean; search: string; setSearch: (s: string) => void;
  onAddCatalog: (catId: string, roomKey: string | null) => void;
  onAddCustom: (name: string, roomKey: string | null) => void;
  onQty: (item: ClientItem, n: number) => void;
  onRetry: (key: string) => void;
  onNote: (key: string, text: string) => void;
  total: (catId: string) => number;
  onSetTotal: (catId: string, n: number) => void;
  kit: SearchKit;
}) {
  const section = wholeMoveSection(inv);
  const inSection = useMemo(() => sectionIds(inv), [inv]);

  // EVERY item, wherever it was filed. The full-list path has no rooms, and
  // a client who started room-by-room and switched must still see what they
  // entered — otherwise switching looks like it threw their work away.
  // Boxes / Misc items are the exception: they are counted in that section,
  // first, and not listed a second time down here.
  const all = useMemo(
    () => [...inv.rooms.flatMap((r) => r.items), ...inv.unroomed]
      .filter((it) => !(it.catalogItemId && inSection.has(it.catalogItemId))),
    [inv.rooms, inv.unroomed, inSection],
  );

  const q = search.trim();
  // Ranked: exact, then synonym, then typo matches. An item ALREADY on the list
  // still appears — marked, and counted up when tapped — so "fridge" never
  // reads as "not found" just because the fridge is already there.
  const hits = useMemo(() => (q ? searchCatalog(kit.prepared, q).slice(0, 24) : []), [q, kit.prepared]);
  useZeroResultReport(search, hits.length, kit.reportMiss);

  const pick = (catId: string) => {
    if (inSection.has(catId) || total(catId) > 0) onSetTotal(catId, total(catId) + 1);
    else onAddCatalog(catId, null);
    setSearch('');
  };

  return (
    <>
      {section && (
        <WholeMoveSection
          inv={inv} section={section} busy={busy} total={total}
          onSetTotal={onSetTotal} onRetry={onRetry} onNote={onNote}
        />
      )}

      <div className="pp-card">
        <div className="pp-sec">Type or paste a list</div>
        <NotesBox
          label="What you're moving"
          hint="One thing per line is easiest — “3-seat sofa, 2 armchairs, queen bed…”. Rough is fine; we'll tidy it up."
          value={inv.notes[WRITTEN_LIST] ?? ''}
          disabled={busy}
          onSave={(v) => onNote(WRITTEN_LIST, v)}
        />
      </div>

      <div className="pp-card">
        <div className="pp-sec">Or add items one by one</div>
        <input
          className="ci-search" type="search" value={search}
          placeholder="Search for an item — couch, fridge, boxes…"
          onChange={(e) => setSearch(e.target.value)}
        />

        {q && (
          hits.length > 0 ? (
            <div className="ci-grid" style={{ marginBottom: 12 }}>
              {hits.map(({ item: c }) => {
                const n = total(c.id);
                return (
                  <button key={c.id} type="button" className={`ci-tile${n > 0 ? ' on' : ''}`} onClick={() => pick(c.id)}>
                    <CatalogArt src={c.imageUrl} icon={c.icon} size={ART} />
                    <span className="ci-tile-n">{c.name}</span>
                    {n > 0 && <span className="ci-ours">On your list · {n}</span>}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ marginBottom: 12 }}>
              <NoMatch query={q} onAdd={() => { kit.reportMiss(q, 'add_own'); onAddCustom(q, null); setSearch(''); }} />
            </div>
          )
        )}

        <AddYourOwn placeholder="Add something by name…" onAdd={(n) => onAddCustom(n, null)} />

        {all.length > 0 && (
          <div className="ci-list" style={{ marginTop: 14 }}>
            {all.map((it) => <LineRow key={it.id} item={it} onQty={onQty} onRetry={onRetry} />)}
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Submitted — read only, plus the way back in
// ---------------------------------------------------------------------------

function SubmittedSummary({ inv, token, call, onChanged }: {
  inv: ClientInventory;
  token: string;
  call: (path: string, init?: RequestInit) => Promise<any>;
  onChanged: (inv: ClientInventory) => void;
}) {
  const [asking, setAsking] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Boxes / Misc first, one line per item with its total for the move; those
  // rows then left out of the rooms and the loose list below.
  const section = wholeMoveSection(inv);
  const inSection = sectionIds(inv);
  const notSection = (it: ClientItem) => !(it.catalogItemId && inSection.has(it.catalogItemId));
  const sectionLines = section
    ? inv.catalog.filter((c) => c.category === section.key)
      .map((c) => ({ c, qty: catalogRows(inv, c.id).reduce((n, r) => n + r.quantity, 0) }))
      .filter((l) => l.qty > 0)
    : [];
  const roomsWithItems = inv.rooms
    .filter((r) => !r.wholeMove)
    .map((r) => ({ ...r, items: r.items.filter(notSection) }))
    .filter((r) => r.items.length > 0);
  const looseItems = inv.unroomed.filter(notSection);
  const writtenList = inv.notes[WRITTEN_LIST] ?? '';
  const general = inv.notes[GENERAL] ?? '';

  const sendRequest = async () => {
    setBusy(true); setErr('');
    try {
      await call('/request-change', { method: 'POST', body: JSON.stringify({ message }) });
      const d = await call('');
      onChanged(d.inventory);
      setAsking(false);
      setMessage('');
    } catch (e: any) {
      setErr(e?.message || 'We could not send that. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="pp-card accent">
        <div className="pp-inv-row">
          <div>
            <div className="pp-sec" style={{ margin: 0 }}>Inventory received</div>
            <div style={{ fontSize: 14, marginTop: 4 }}>
              Thank you — we have your list of {inv.itemCount} item{inv.itemCount === 1 ? '' : 's'}.
              We'll use it to finalize your estimate and will be in touch.
            </div>
          </div>
          <span className="pp-badge ok">Sent {formatDateTime(inv.submittedAt!)}</span>
        </div>
      </div>

      {/* THE CHANGE REQUEST DOES NOT UNLOCK ANYTHING, and the wording says so.
          A rep reopens it; telling the client the list is editable again when it
          is not is the kind of small lie that produces a phone call. */}
      {inv.changeRequestedAt ? (
        <div className="pp-card">
          <div className="pp-callout">
            <Icons.check size={20} />
            <div>
              <div className="pp-callout-t">We got your request</div>
              <div className="pp-callout-b">
                Someone will reopen your list shortly — you'll be able to edit it here as soon as they do.
                Asked {formatDateTime(inv.changeRequestedAt)}.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="pp-card">
          {asking ? (
            <>
              <div className="pp-sec">What needs changing?</div>
              <label className="ci-note">
                <span>Tell us what to add, remove or fix</span>
                <textarea
                  value={message} disabled={busy} autoFocus
                  placeholder="We decided to leave the piano behind…"
                  onChange={(e) => setMessage(e.target.value)}
                />
              </label>
              {err && <div className="ci-state err">{err}</div>}
              <PortalActions>
                <button type="button" className="pp-btn primary" disabled={busy} onClick={sendRequest}>
                  Send request
                </button>
                <button type="button" className="pp-btn pp-btn-quiet" disabled={busy} onClick={() => setAsking(false)}>
                  Cancel
                </button>
              </PortalActions>
            </>
          ) : (
            <>
              <div className="pp-sec">Need to change something?</div>
              <p className="pp-sub">
                Your list is locked while we price it. Ask us to reopen it and we'll do that for you.
              </p>
              <PortalActions>
                <button type="button" className="pp-btn" onClick={() => setAsking(true)}>
                  <Icons.edit size={18} /> Request a change
                </button>
              </PortalActions>
            </>
          )}
        </div>
      )}

      {writtenList.trim() && (
        <div className="pp-card">
          <div className="pp-sec">The list you typed</div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{writtenList}</div>
        </div>
      )}

      {section && sectionLines.length > 0 && (
        <div className="pp-card">
          <div className="pp-sec" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <RoomIcon name={section.icon || 'box'} size={18} stroke={1.8} /> {section.name}
          </div>
          <div className="ci-list">
            {sectionLines.map(({ c, qty }) => (
              <div key={c.id} className="ci-line">
                <CatalogArt src={c.imageUrl} icon={c.icon} size={ART_LINE} stroke={1.8} />
                <div className="ci-line-m"><div className="ci-line-n">{c.name}</div></div>
                <span className="pp-qty">×{qty}</span>
              </div>
            ))}
          </div>
          {(inv.notes[section.key] ?? '').trim() && (
            <div className="pp-rule" style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>{inv.notes[section.key]}</div>
          )}
        </div>
      )}

      {roomsWithItems.map((room) => (
        <div key={room.key} className="pp-card">
          <div className="pp-inv-row" style={{ marginBottom: 10 }}>
            <div className="pp-sec" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <RoomIcon name={room.icon} size={18} stroke={1.8} /> {room.name}
            </div>
          </div>
          <div className="ci-list">
            {room.items.map((it) => <ReadOnlyLine key={it.id} item={it} />)}
          </div>
          {(inv.notes[room.key] ?? '').trim() && (
            <div className="pp-rule" style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>{inv.notes[room.key]}</div>
          )}
        </div>
      ))}

      {looseItems.length > 0 && (
        <div className="pp-card">
          <div className="pp-sec">Items</div>
          <div className="ci-list">
            {looseItems.map((it) => <ReadOnlyLine key={it.id} item={it} />)}
          </div>
        </div>
      )}

      {general.trim() && (
        <div className="pp-card">
          <div className="pp-sec">Your notes</div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{general}</div>
        </div>
      )}

      {/* Read-only from here: the list is locked, and so is what was sent with it. */}
      <MediaSection media={inv.media} token={token} readOnly onChanged={() => {}} />
    </>
  );
}

function ReadOnlyLine({ item }: { item: ClientItem }) {
  return (
    <div className="ci-line">
      <CatalogArt src={item.imageUrl} icon={item.icon} size={ART_LINE} stroke={1.8} />
      <div className="ci-line-m">
        <div className="ci-line-n">{item.name}</div>
        {item.notes && <div className="ci-line-s">{item.notes}</div>}
      </div>
      <span className="pp-qty">×{item.quantity}</span>
    </div>
  );
}
